import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { 
  Newspaper, TrendingUp, TrendingDown, Minus, RefreshCw, AlertTriangle,
  ThumbsUp, ThumbsDown, Activity, Globe, Clock, ArrowUpRight, ArrowDownRight
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const COMMODITIES = [
  { id: "cocoa", name: "Cocoa", icon: "🍫" },
  { id: "coffee", name: "Coffee", icon: "☕" },
  { id: "gold", name: "Gold", icon: "🥇" },
  { id: "crude_oil", name: "Crude Oil", icon: "🛢️" },
  { id: "wheat", name: "Wheat", icon: "🌾" },
  { id: "copper", name: "Copper", icon: "🔶" },
];

const MOCK_NEWS = [
  {
    id: 1,
    headline: "Gold prices surge amid global economic uncertainty",
    source: "Reuters",
    time: "2 hours ago",
    sentiment: "bullish",
    impact: "high",
    summary: "Gold reached new highs as investors seek safe-haven assets amid inflation concerns.",
  },
  {
    id: 2,
    headline: "Coffee futures rise on supply chain disruptions in Brazil",
    source: "Bloomberg",
    time: "4 hours ago",
    sentiment: "bullish",
    impact: "medium",
    summary: "Adverse weather conditions affect Brazilian coffee production, pushing prices higher.",
  },
  {
    id: 3,
    headline: "Crude oil stabilizes after OPEC+ maintains production levels",
    source: "Financial Times",
    time: "6 hours ago",
    sentiment: "neutral",
    impact: "medium",
    summary: "Oil markets find equilibrium as OPEC+ decides to keep current output targets.",
  },
  {
    id: 4,
    headline: "Copper demand outlook weakens on China construction slowdown",
    source: "WSJ",
    time: "8 hours ago",
    sentiment: "bearish",
    impact: "high",
    summary: "Industrial metal faces headwinds as Chinese real estate sector continues to struggle.",
  },
  {
    id: 5,
    headline: "Wheat prices decline on favorable harvest forecasts",
    source: "Agri News",
    time: "10 hours ago",
    sentiment: "bearish",
    impact: "medium",
    summary: "Global wheat production expected to exceed previous estimates this season.",
  },
];

export default function NewsSentimentPage() {
  const [selectedCommodity, setSelectedCommodity] = useState("all");
  const [sentimentData, setSentimentData] = useState(null);
  const [news, setNews] = useState(MOCK_NEWS);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  const fetchSentimentAnalysis = useCallback(async () => {
    setLoading(true);
    try {
      const commodity = selectedCommodity === "all" ? "commodities market" : selectedCommodity;
      const res = await api.post("/ai/news-sentiment", { commodity });
      setSentimentData(res.data);
    } catch (err) {
      console.error("Error fetching sentiment:", err);
      // Use mock data if API fails
      setSentimentData({
        overall_sentiment: "bullish",
        sentiment_score: 68,
        confidence: "high",
        bullish_factors: [
          "Strong demand from emerging markets",
          "Supply constraints due to weather",
          "Weak dollar supporting prices",
        ],
        bearish_factors: [
          "Potential recession concerns",
          "High inventory levels in some regions",
        ],
        market_impact: "Expect moderate volatility with upward bias in the short term.",
        key_events: [
          { event: "OPEC+ Meeting", date: "Next Week", impact: "high" },
          { event: "US Fed Decision", date: "In 2 weeks", impact: "medium" },
          { event: "China Trade Data", date: "Tomorrow", impact: "medium" },
        ],
      });
    } finally {
      setLoading(false);
    }
  }, [selectedCommodity]);

  useEffect(() => {
    fetchSentimentAnalysis();
  }, [fetchSentimentAnalysis]);

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case "bullish": return "text-[#00ff41]";
      case "bearish": return "text-[#ff3b30]";
      default: return "text-[#eab308]";
    }
  };

  const getSentimentBg = (sentiment) => {
    switch (sentiment) {
      case "bullish": return "bg-[#00ff41]/10 border-[#00ff41]/30";
      case "bearish": return "bg-[#ff3b30]/10 border-[#ff3b30]/30";
      default: return "bg-[#eab308]/10 border-[#eab308]/30";
    }
  };

  const getSentimentIcon = (sentiment) => {
    switch (sentiment) {
      case "bullish": return <TrendingUp className="w-5 h-5 text-[#00ff41]" />;
      case "bearish": return <TrendingDown className="w-5 h-5 text-[#ff3b30]" />;
      default: return <Minus className="w-5 h-5 text-[#eab308]" />;
    }
  };

  const getImpactBadge = (impact) => {
    switch (impact) {
      case "high": return <Badge className="bg-[#ff3b30]/10 text-[#ff3b30] border-[#ff3b30]/30 rounded-none text-[9px]">HIGH</Badge>;
      case "medium": return <Badge className="bg-[#eab308]/10 text-[#eab308] border-[#eab308]/30 rounded-none text-[9px]">MED</Badge>;
      default: return <Badge className="bg-zinc-800 text-zinc-400 border-zinc-700 rounded-none text-[9px]">LOW</Badge>;
    }
  };

  const filteredNews = selectedCommodity === "all" 
    ? news 
    : news.filter(n => n.headline.toLowerCase().includes(selectedCommodity.replace("_", " ")));

  return (
    <div data-testid="news-sentiment-page" className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-wider text-zinc-100">
            News Sentiment
          </h1>
          <p className="text-xs text-zinc-500 font-mono mt-1">
            AI-powered market news analysis
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={selectedCommodity} onValueChange={setSelectedCommodity}>
            <SelectTrigger className="w-40 rounded-none border-zinc-700 bg-zinc-950">
              <SelectValue placeholder="All Markets" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700 rounded-none">
              <SelectItem value="all">All Markets</SelectItem>
              {COMMODITIES.map(c => (
                <SelectItem key={c.id} value={c.id} className="font-mono">
                  {c.icon} {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button
            variant="outline"
            size="sm"
            className="rounded-none border-zinc-700 bg-zinc-950"
            onClick={fetchSentimentAnalysis}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Sentiment Overview */}
      {sentimentData && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Main Sentiment Card */}
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none lg:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-sm uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                <Activity className="w-4 h-4" />
                Overall Market Sentiment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-6">
                <div className={`p-4 border rounded-none ${getSentimentBg(sentimentData.overall_sentiment)}`}>
                  {getSentimentIcon(sentimentData.overall_sentiment)}
                </div>
                <div>
                  <p className={`font-heading text-3xl font-bold uppercase ${getSentimentColor(sentimentData.overall_sentiment)}`}>
                    {sentimentData.overall_sentiment}
                  </p>
                  <p className="text-xs text-zinc-500 font-mono">
                    Confidence: {sentimentData.confidence}
                  </p>
                </div>
                <div className="flex-1 max-w-xs">
                  <div className="flex justify-between text-xs font-mono mb-1">
                    <span className="text-zinc-500">Sentiment Score</span>
                    <span className={getSentimentColor(sentimentData.overall_sentiment)}>
                      {sentimentData.sentiment_score}%
                    </span>
                  </div>
                  <Progress 
                    value={sentimentData.sentiment_score} 
                    className="h-2 bg-zinc-800"
                  />
                </div>
              </div>
              
              <div className="p-3 bg-zinc-900/50 border border-zinc-800">
                <p className="text-sm text-zinc-300">{sentimentData.market_impact}</p>
              </div>
            </CardContent>
          </Card>

          {/* Key Events */}
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-sm uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Upcoming Events
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {sentimentData.key_events?.map((event, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-zinc-900/30 border border-zinc-800">
                    <div>
                      <p className="text-sm text-zinc-200 font-mono">{event.event}</p>
                      <p className="text-xs text-zinc-500">{event.date}</p>
                    </div>
                    {getImpactBadge(event.impact)}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Factors */}
      {sentimentData && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-sm uppercase tracking-wider text-[#00ff41] flex items-center gap-2">
                <ThumbsUp className="w-4 h-4" />
                Bullish Factors
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {sentimentData.bullish_factors?.map((factor, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                    <ArrowUpRight className="w-4 h-4 text-[#00ff41] flex-shrink-0 mt-0.5" />
                    {factor}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-sm uppercase tracking-wider text-[#ff3b30] flex items-center gap-2">
                <ThumbsDown className="w-4 h-4" />
                Bearish Factors
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {sentimentData.bearish_factors?.map((factor, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                    <ArrowDownRight className="w-4 h-4 text-[#ff3b30] flex-shrink-0 mt-0.5" />
                    {factor}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      )}

      {/* News Feed */}
      <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-sm uppercase tracking-wider text-zinc-400 flex items-center gap-2">
            <Newspaper className="w-4 h-4" />
            Latest News
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px]">
            <div className="space-y-3">
              {filteredNews.map((item) => (
                <div
                  key={item.id}
                  className="p-4 bg-zinc-900/30 border border-zinc-800 hover:border-zinc-700 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {getSentimentIcon(item.sentiment)}
                        <Badge className={`rounded-none text-[9px] ${getSentimentBg(item.sentiment)}`}>
                          {item.sentiment.toUpperCase()}
                        </Badge>
                        {getImpactBadge(item.impact)}
                      </div>
                      <h3 className="text-sm font-medium text-zinc-100 mb-1">{item.headline}</h3>
                      <p className="text-xs text-zinc-400 mb-2">{item.summary}</p>
                      <div className="flex items-center gap-3 text-[10px] text-zinc-500">
                        <span className="flex items-center gap-1">
                          <Globe className="w-3 h-3" />
                          {item.source}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {item.time}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
